import { doRequest } from "../../../functions/request";
import { LARGURA_SAIDA, ALTURA_SAIDA } from "./tema";
// Importa pelo efeito colateral: é este módulo que registra o `exporting`,
// de onde vem o chart.getSVG() usado aqui.
import "./highcharts";

/**
 * Rasteriza um gráfico do Highcharts num JPEG do tamanho exato de saída.
 *
 * Por que este caminho e não o módulo `offline-exporting`: aquele dispara
 * um download do arquivo, e o que precisamos aqui é do Blob em mãos para
 * mandar pro banco de imagens. Com getSVG + canvas temos o Blob e a
 * dimensão exata, independente do tamanho que o preview tem na tela.
 *
 * Validado com o Highcharts real do repo (11.4.8): sai 604x366 exatos, o
 * canvas não fica tainted (nenhum recurso externo entra no SVG) e o título
 * multicolorido sobrevive como <tspan> nativo.
 */
export async function gerarJpeg(chart, largura = LARGURA_SAIDA, altura = ALTURA_SAIDA) {
  if (!chart || typeof chart.getSVG !== "function") {
    throw new Error("Gráfico ainda não está pronto para exportar.");
  }

  const svg = chart.getSVG({ chart: { width: largura, height: altura } });

  // O sanitizeSVG do Highcharts corta tudo que vem depois de </svg>: um
  // rótulo com useHTML aparece no preview e some da imagem, sem erro
  // nenhum. Melhor quebrar aqui do que publicar um gráfico sem o texto.
  if (svg.includes("<foreignObject")) {
    throw new Error(
      "Algum elemento do gráfico está usando useHTML — ele não sobrevive à exportação."
    );
  }
  // Referência externa não carrega dentro de um SVG em <img> e ainda
  // contamina o canvas, fazendo o toBlob falhar lá na frente.
  if (/(?:href|src)\s*=\s*"(?:https?:)?\/\//i.test(svg)) {
    throw new Error(
      "O gráfico tem uma imagem externa; use apenas cores e formas."
    );
  }

  const urlSvg = URL.createObjectURL(
    new Blob([svg], { type: "image/svg+xml;charset=utf-8" })
  );

  try {
    const imagem = new Image();
    imagem.src = urlSvg;
    await imagem.decode();

    const canvas = document.createElement("canvas");
    canvas.width = largura;
    canvas.height = altura;
    const ctx = canvas.getContext("2d");
    // O JPEG não tem transparência: sem pintar o fundo antes, área não
    // desenhada sairia preta por acaso em vez de por decisão.
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, largura, altura);
    ctx.drawImage(imagem, 0, 0, largura, altura);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.92)
    );
    if (!blob) throw new Error("Não foi possível converter o gráfico em imagem.");

    // Confere o tamanho decodificando o JPEG de volta. É a checagem que
    // pega qualquer regressão de dimensão de uma vez só — inclusive as que
    // aparecem só com zoom do navegador ou em tela HiDPI.
    const conferencia = new Image();
    const urlBlob = URL.createObjectURL(blob);
    try {
      conferencia.src = urlBlob;
      await conferencia.decode();
      if (
        conferencia.naturalWidth !== largura ||
        conferencia.naturalHeight !== altura
      ) {
        throw new Error(
          `A imagem saiu em ${conferencia.naturalWidth}x${conferencia.naturalHeight}, esperado ${largura}x${altura}.`
        );
      }
    } finally {
      URL.revokeObjectURL(urlBlob);
    }

    return blob;
  } finally {
    URL.revokeObjectURL(urlSvg);
  }
}

/**
 * Nome do arquivo no banco de imagens.
 *
 * Precisa começar com "gf01" — é o prefixo que classifica a imagem na
 * editoria Gráficos (o servidor deriva img_editoria daí quando o cliente
 * não manda). E precisa ser único por conta própria: o uploadImg.js grava
 * exatamente o nome que o cliente mandar, sem deduplicar, então um nome
 * repetido sobrescreve silenciosamente uma imagem já publicada.
 */
export function nomeArquivoGrafico() {
  const aleatorio = Math.random().toString(36).slice(2, 8);
  return `gf01gerador-${Date.now()}-${aleatorio}.jpg`;
}

/**
 * Envia o JPEG para o banco de imagens e devolve o nome do arquivo e a URL.
 *
 * A ordem dos campos do FormData é obrigatória: o `filename` do multer faz
 * JSON.parse(req.body.newData), então o campo de texto precisa já ter sido
 * lido do stream quando a parte binária chegar. Invertendo a ordem, o
 * upload falha com um 500 genérico.
 */
export async function salvarNoBanco(blob, meta = {}) {
  const nomeArquivo = nomeArquivoGrafico();

  const dados = {
    img_editoria: "gf01",
    img_pais: meta.pais ?? null,
    img_ordem: meta.ordem ?? null,
    img_nome: meta.legenda || "Rural Business",
    img_comentario: meta.comentario || "",
    img_arquivo: nomeArquivo,
  };

  const campos = new FormData();
  campos.append("newData", JSON.stringify(dados));
  campos.append("fileData", blob, nomeArquivo);

  const resposta = await doRequest("/imagem/insertImagem", {
    method: "POST",
    body: campos,
  });

  if (!resposta?.success) {
    throw new Error("O servidor recusou o envio da imagem.");
  }

  return { nomeArquivo, url: `${process.env.REACT_APP_BANCO_IMG_URL}${nomeArquivo}` };
}

/**
 * HTML que vai para a matéria.
 *
 * A imagem é gerada em 604 para ficar nítida em tela retina, mas entra na
 * matéria com width=302 — o mesmo espaço que os gráficos sempre ocuparam,
 * então nada muda no layout do que já está publicado. Para que os gráficos
 * apareçam maiores no site, basta remover o atributo width.
 */
export function montarHtmlImagem(url, legenda = "") {
  return `<p><img src="${url}" alt="${legenda}" width="302" style="max-width:100%;" /></p>`;
}

/** Baixa o JPEG direto, sem passar pelo banco (mesmo padrão do Clima.js). */
export function baixarJpeg(blob, nome = "grafico") {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${String(nome).replace(/\s+/g, "_")}_${new Date()
    .toISOString()
    .slice(0, 10)}.jpg`;
  a.click();
  URL.revokeObjectURL(url);
}
